import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import {
  Clock,
  Calendar,
  Phone,
  Timer,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Activity,
  FileText,
  CheckCircle2,
  Edit3,
  Send,
  ClipboardCheck,
  ArrowRight,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth, UserRole } from '../contexts/AuthContext';
import { timesheetService } from '../services/timesheetService';
import { documentsService } from '../services/documentsService';
import { DashboardStats } from '@timesheet/schemas';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert } from '../components/ui/alert';
import { cn } from '../lib/utils';
import { PageTransition, staggerContainer, fadeInUp } from '../components/PageTransition';
import {
  TimeframeSelector,
  TimeframeValue,
  HoursBarChart,
  ApprovalDonutChart,
  MonthlyTotalsTable,
  TeamMemberChart,
  StatCardsSkeleton,
  HoursBarChartSkeleton,
  ApprovalDonutChartSkeleton,
  MonthlyTotalsTableSkeleton,
  TeamMemberChartSkeleton,
} from '../components/charts';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  className?: string;
  suffix?: string;
  trend?: {
    value: number;
    isPositive?: boolean;
  };
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, className, suffix, trend }) => (
  <motion.div variants={fadeInUp} transition={{ duration: 0.3 }}>
    <Card className={cn('transition-shadow hover:shadow-md', className)}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold tracking-tight">
              {value}
              {suffix && <span className="text-lg font-normal text-muted-foreground">{suffix}</span>}
            </p>
            {trend && (
              <div className={cn(
                'flex items-center gap-1 text-xs font-medium',
                trend.isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              )}>
                {trend.isPositive ? (
                  <TrendingUp className="h-3 w-3" />
                ) : (
                  <TrendingDown className="h-3 w-3" />
                )}
                <span>{trend.isPositive ? '+' : ''}{trend.value}% vs last period</span>
              </div>
            )}
          </div>
          <div className="h-12 w-12 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

// Activity item types
interface ActivityItem {
  id: number;
  type: 'timesheet_submitted' | 'timesheet_approved' | 'timesheet_edited' | 'order_assigned';
  title: string;
  description: string;
  time: string;
  icon: React.ReactNode;
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingApprovals, setPendingApprovals] = useState<{ count: number; userCount: number }>({ count: 0, userCount: 0 });
  const [payslipsCount, setPayslipsCount] = useState(0);
  const [lastPayslipDate, setLastPayslipDate] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState<TimeframeValue>('month');
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>({
    start: new Date(),
    end: new Date(),
  });

  // Time-aware greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dashboard.greetings.morning', 'Good morning');
    if (hour < 17) return t('dashboard.greetings.afternoon', 'Good afternoon');
    return t('dashboard.greetings.evening', 'Good evening');
  };

  const firstName = user?.name?.split(' ')[0] || user?.name || '';

  // Mock recent activities (in production, this would come from an API)
  const recentActivities: ActivityItem[] = [
    {
      id: 1,
      type: 'timesheet_submitted',
      title: t('activity.timesheetSubmitted', 'Timesheet Submitted'),
      description: t('activity.timesheetSubmittedDesc', '{{month}} timesheet submitted for approval', { month: 'December 2025' }),
      time: t('activity.time.hoursAgo', '{{count}} hours ago', { count: 2 }),
      icon: <Send className="h-4 w-4 text-blue-500" />,
    },
    {
      id: 2,
      type: 'timesheet_approved',
      title: t('activity.timesheetApproved', 'Timesheet Approved'),
      description: t('activity.timesheetApprovedDesc', '{{month}} timesheet was approved', { month: 'November 2025' }),
      time: t('activity.time.daysAgo', '{{count}} days ago', { count: 3 }),
      icon: <CheckCircle2 className="h-4 w-4 text-green-500" />,
    },
    {
      id: 3,
      type: 'timesheet_edited',
      title: t('activity.hoursLogged', 'Hours Logged'),
      description: t('activity.hoursLoggedDesc', 'Added {{hours}} hours to Order #{{order}}', { hours: 8, order: 'ABC-123' }),
      time: t('activity.time.daysAgo', '{{count}} days ago', { count: 5 }),
      icon: <Edit3 className="h-4 w-4 text-orange-500" />,
    },
    {
      id: 4,
      type: 'order_assigned',
      title: t('activity.orderAssigned', 'New Order Assigned'),
      description: t('activity.orderAssignedDesc', 'You have been assigned to Order #{{order}}', { order: 'DEF-456' }),
      time: t('activity.time.weeksAgo', '{{count}} week ago', { count: 1 }),
      icon: <FileText className="h-4 w-4 text-purple-500" />,
    },
  ];

  const fetchDashboardStats = useCallback(async (startDate: string, endDate: string) => {
    try {
      setLoading(true);
      const response = await timesheetService.getDashboardStats(startDate, endDate);
      setStats(response);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError(t('dashboard.errors.fetchStats'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Handle timeframe change
  const handleTimeframeChange = useCallback((value: TimeframeValue, start: Date, end: Date) => {
    setTimeframe(value);
    setDateRange({ start, end });
    const startStr = format(start, 'yyyy-MM-dd');
    const endStr = format(end, 'yyyy-MM-dd');
    fetchDashboardStats(startStr, endStr);
  }, [fetchDashboardStats]);

  const isManagerOrAdmin = Boolean(
    user?.role && [UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role),
  );

  useEffect(() => {
    // Fetch pending approvals for managers/admins
    if (isManagerOrAdmin) {
      timesheetService.getPendingCount()
        .then((data) => setPendingApprovals(data))
        .catch(() => setPendingApprovals({ count: 0, userCount: 0 }));
    }

    // My payslips (count + latest) from the documents summary
    documentsService
      .summary()
      .then((summary) => {
        setPayslipsCount(summary.myPayslips.count);
        setLastPayslipDate(summary.myPayslips.latest ? format(new Date(summary.myPayslips.latest.createdAt), 'd MMM yyyy') : null);
      })
      .catch(() => {
        setPayslipsCount(0);
        setLastPayslipDate(null);
      });
  }, [user?.id, isManagerOrAdmin]);

  // Transform data for charts
  const hoursByProjectData = stats?.hoursByProject?.map((p) => ({
    label: p.orderCode,
    hours: p.hours,
  })) || [];

  const monthlyTotalsData = stats?.monthlyTotals?.map((m) => ({
    month: m.month,
    hours: m.hours,
    days: m.days,
    overtime: m.overtime,
    onCall: m.onCall,
    approvedCount: m.approvedCount,
    pendingCount: m.pendingCount,
  })) || [];

  const teamMemberData = stats?.teamStats?.hoursByUser?.map((u) => ({
    userId: u.userId,
    userName: u.userName,
    hours: u.hours,
  })) || [];

  // Get timeframe label for display
  const getTimeframeLabel = () => {
    switch (timeframe) {
      case 'week':
        return t('dashboard.thisWeek', 'This Week');
      case 'month':
        return t('dashboard.thisMonth', 'This Month');
      case '3months':
        return t('dashboard.last3Months', 'Last 3 Months');
      case 'year':
        return t('dashboard.thisYear', 'This Year');
      default:
        return format(dateRange.start, 'MMM yyyy');
    }
  };

  if (error && !stats) {
    return (
      <div className="p-6">
        <Alert variant="error">{error}</Alert>
      </div>
    );
  }

  return (
    <PageTransition className="p-4 sm:p-6 space-y-6">
      {/* Greeting Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="space-y-1"
      >
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
          {getGreeting()}, {firstName}!
        </h1>
        <p className="text-muted-foreground">
          {t('dashboard.subtitle', "Here's what's happening with your timesheet.")}
        </p>
      </motion.div>

      {/* Section Header with Timeframe Selector */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
            <BarChart3 className="h-5 w-5 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold tracking-tight">{t('dashboard.title')}</h2>
            <p className="text-sm text-muted-foreground">
              {getTimeframeLabel()} {t('dashboard.summary', 'Summary')}
            </p>
          </div>
        </div>
        <TimeframeSelector
          value={timeframe}
          onChange={handleTimeframeChange}
        />
      </motion.div>

      {/* Stats Grid - Shows skeleton when loading */}
      {loading ? (
        <StatCardsSkeleton />
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
        >
          <StatCard
            title={t('dashboard.totalWorkingHours')}
            value={stats?.totalWorkingHours?.toFixed(1) ?? '0'}
            icon={<Clock className="h-6 w-6 text-orange-600 dark:text-orange-400" />}
          />

          <StatCard
            title={t('dashboard.totalWorkingDays')}
            value={stats?.totalWorkingDays ?? 0}
            icon={<Calendar className="h-6 w-6 text-orange-600 dark:text-orange-400" />}
          />

          <StatCard
            title={t('dashboard.totalOvertimeHours')}
            value={(stats?.totalOvertimeHours ?? 0).toFixed(1)}
            icon={<Timer className="h-6 w-6 text-orange-600 dark:text-orange-400" />}
          />

          <StatCard
            title={t('dashboard.totalOnCallHours')}
            value={(stats?.totalOnCallHours ?? 0).toFixed(1)}
            icon={<Phone className="h-6 w-6 text-orange-600 dark:text-orange-400" />}
          />
        </motion.div>
      )}

      {/* Pending Approvals Card - Manager/Admin/Accountant */}
      {isManagerOrAdmin && pendingApprovals.count > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
        >
          <Card className="border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-950/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-full bg-orange-100 dark:bg-orange-900/50 flex items-center justify-center">
                    <ClipboardCheck className="h-7 w-7 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">
                      {t('dashboard.pendingApprovals', 'Pending Approvals')}
                    </h3>
                    <p className="text-muted-foreground">
                      {t('dashboard.pendingApprovalsDesc', '{{count}} entries from {{users}} users awaiting review', {
                        count: pendingApprovals.count,
                        users: pendingApprovals.userCount,
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-4xl font-bold text-orange-600 dark:text-orange-400">
                      {pendingApprovals.count}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('dashboard.entries', 'entries')}
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate('/approvals')}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    {t('dashboard.reviewNow', 'Review')}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Payslips Recap Card */}
      {payslipsCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
        >
          <Card className="border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-950/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-full bg-orange-100 dark:bg-orange-900/50 flex items-center justify-center">
                    <FileText className="h-7 w-7 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">
                      {t('dashboard.payslips', 'Payslips')}
                    </h3>
                    <p className="text-muted-foreground">
                      {t('dashboard.payslipsDesc', '{{count}} payslips', { count: payslipsCount })}
                      {lastPayslipDate && ` • ${t('dashboard.lastPayslip', 'Latest:')} ${lastPayslipDate}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-4xl font-bold text-orange-600 dark:text-orange-400">
                      {payslipsCount}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('dashboard.payslipsLabel', 'payslips')}
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate('/documents?tab=BUSTE_PAGA')}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    {t('dashboard.viewPayslips', 'View')}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Charts Row - Hours by Project and Approval Status */}
      {loading ? (
        <div className="grid gap-6 md:grid-cols-2">
          <HoursBarChartSkeleton />
          <ApprovalDonutChartSkeleton />
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="grid gap-6 md:grid-cols-2"
        >
          <HoursBarChart
            data={hoursByProjectData}
            title={t('dashboard.hoursByProject', 'Hours by Project')}
            maxBars={8}
          />
          <ApprovalDonutChart
            draft={stats?.approvalBreakdown?.draft ?? 0}
            pending={stats?.approvalBreakdown?.in_approval ?? 0}
            approved={stats?.approvalBreakdown?.approved ?? 0}
            rejected={stats?.approvalBreakdown?.rejected ?? 0}
          />
        </motion.div>
      )}

      {/* Team Member Chart - Manager/Admin only */}
      {isManagerOrAdmin && (
        loading ? (
          <TeamMemberChartSkeleton />
        ) : teamMemberData.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.25 }}
          >
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="h-4 w-4 text-orange-500" />
                  {t('dashboard.teamOverview', 'Team Overview')}
                  {stats?.teamStats && (
                    <span className="text-sm font-normal text-muted-foreground ml-2">
                      ({stats.teamStats.activeUsers} {t('dashboard.activeUsers', 'active users')})
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6 lg:grid-cols-2">
                  <TeamMemberChart data={teamMemberData} />
                  {stats?.teamStats?.totalNetProfit !== undefined && (
                    <Card className="border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20">
                      <CardContent className="p-6 flex flex-col justify-center h-full">
                        <div className="text-center">
                          <p className="text-sm font-medium text-muted-foreground mb-2">
                            {t('dashboard.totalTeamProfit', 'Total Team Net Profit')}
                          </p>
                          <p className="text-4xl font-bold text-green-600 dark:text-green-400">
                            €{stats.teamStats.totalNetProfit.toFixed(2)}
                          </p>
                          <p className="text-sm text-muted-foreground mt-2">
                            {getTimeframeLabel()}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )
      )}

      {/* Monthly Totals Table */}
      {loading ? (
        <MonthlyTotalsTableSkeleton />
      ) : monthlyTotalsData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <MonthlyTotalsTable data={monthlyTotalsData} />
        </motion.div>
      )}

      {/* Recent Activity */}
      {/*
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.35 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-orange-500" />
              {t('dashboard.recentActivity', 'Recent Activity')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivities.map((activity, index) => (
                <motion.div
                  key={activity.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.05 }}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    {activity.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{activity.title}</p>
                    <p className="text-sm text-muted-foreground truncate">{activity.description}</p>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{activity.time}</span>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>*/}
    </PageTransition>
  );
};

export default Dashboard;
